<template>
    <div class="lead-preview-widget">
        <div class="preview-header">
            <div class="header-content">
                <div class="header-icon">
                    <img src="@/assets/admin-icon/menu/leads_active.svg" alt="Lead" />
                </div>
                <div class="header-info">
                    <h3 class="lead-title">{{ leadData.name || 'Lead' }}</h3>
                    <p class="lead-meta">
                        ID: {{ leadData.id }} • Created by {{ lead_item.created_by }} • {{
                            formatDate(lead_item.created_at) }}
                    </p>
                    <p class="lead-meta" v-if="leadData.updated_at">
                        Last updated: {{ formatDate(leadData.updated_at) }}
                    </p>
                </div>
                <div class="priority-badge" v-if="false && leadData.priority !== undefined">
                    <span :class="getPriorityClass(leadData.priority)">
                        {{ getPriorityText(leadData.priority) }}
                    </span>
                </div>
            </div>
        </div>

        <div class="preview-body">
            <div class="lead-details">
                <!-- Basic Lead Info Section -->
                <div class="detail-section">
                    <h4 class="section-title">Lead Details</h4>

                    <div class="detail-row" v-if="leadData.description">
                        <label>Description:</label>
                        <div class="description-content">{{ leadData.description }}</div>
                    </div>

                    <div class="detail-row" v-if="leadData.from">
                        <label>Start Date:</label>
                        <span class="date-range">{{ formatDate(leadData.from) }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.to">
                        <label>End Date:</label>
                        <span class="date-range">{{ formatDate(leadData.to) }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.value">
                        <label>Value:</label>
                        <span class="lead-value">${{ formatValue(leadData.value) }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.percantage !== null && leadData.percantage !== undefined">
                        <label>Percentage:</label>
                        <span class="percentage-badge">{{ leadData.percantage }}%</span>
                    </div>

                    <div class="detail-row" v-if="leadData.attachment">
                        <label>Attachment:</label>
                        <span class="attachment">{{ leadData.attachment }}</span>
                    </div>

                </div>

                <!-- Account Information -->
                <div class="detail-section" v-if="leadData.account">
                    <h4 class="section-title">Account Information</h4>

                    <div class="detail-row">
                        <label>Account:</label>
                        <span class="account-name">{{ leadData.account.name }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.email">
                        <label>Email:</label>
                        <span class="email-link">{{ leadData.account.email }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.phone">
                        <label>Phone:</label>
                        <span>{{ leadData.account.phone }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.website">
                        <label>Website:</label>
                        <span class="website-link">{{ leadData.account.website }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.location">
                        <label>Location:</label>
                        <span>{{ leadData.account.location }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.status">
                        <label>Status:</label>
                        <span :class="getAccountStatusClass(leadData.account.status)">
                            {{ leadData.account.status }}
                        </span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.category">
                        <label>Category:</label>
                        <span class="category-badge">{{ leadData.account.category.replace('_', ' ') }}</span>
                    </div>

                    <div class="detail-row" v-if="leadData.account.worktype">
                        <label>Work Type:</label>
                        <span class="worktype-badge">{{ leadData.account.worktype }}</span>
                    </div>
                </div>

                <!-- Tags Section -->
                <div class="detail-section" v-if="leadData.tags && leadData.tags.length > 0">
                    <h4 class="section-title">Tags</h4>
                    <div class="tags-list">
                        <span class="tag-item" v-for="tag in leadData.tags" :key="tag.id">
                            {{ tag.name }}
                        </span>
                    </div>
                </div>

                <!-- Assignees Section -->
                <div class="detail-section" v-if="leadData.assignees && leadData.assignees.length > 0">
                    <h4 class="section-title">Responsible:</h4>
                    <div class="assignees-list">
                        <div class="assignee-item" v-for="assignee in leadData.assignees" :key="assignee.id">
                            <div class="assignee-avatar">
                                <widgetProfile :title="assignee.first_name + ' ' + assignee.last_name"
                                    :showContent="false" subtitle="" file_name="" path=""></widgetProfile>
                            </div>
                            <div class="assignee-info">
                                <span class="assignee-name">{{ assignee.first_name }} {{ assignee.last_name }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contacts Section -->
                <div class="detail-section" v-if="leadData.contacts && leadData.contacts.length > 0">
                    <h4 class="section-title">Related Contacts</h4>
                    <div class="contacts-list">
                        <div class="contact-item" v-for="contact in leadData.contacts" :key="contact.id">
                            <div class="contact-info">
                                <span class="contact-name">{{ contact.first_name }} {{ contact.last_name }}</span>
                                <span class="contact-email" v-if="contact.email">{{ contact.email }}</span>
                                <span class="contact-title" v-if="contact.title">{{ contact.title }}</span>
                                <div class="contact-details" v-if="contact.phone_alt || contact.worktype">
                                    <span v-if="contact.phone_alt" class="contact-phone">{{ contact.phone_alt }}</span>
                                    <span v-if="contact.worktype" class="contact-worktype">{{ contact.worktype }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "LeadPreviewWidget",
    props: {
        lead_item: {
            type: Object,
            required: true,
        },
    },
    computed: {
        leadData() {
            try {
                return JSON.parse(this.lead_item.content || '{}');
            } catch (error) {
                console.error('Error parsing lead content:', error);
                return {};
            }
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '';
            return this.moment(date).format('MMM DD, YYYY');
        },
        formatValue(value) {
            if (!value) return '0';
            return Number(value).toLocaleString();
        },
        getPriorityClass(priority) {
            const priorityLevel = parseInt(priority);
            if (priorityLevel >= 8) return 'priority-high';
            if (priorityLevel >= 5) return 'priority-medium';
            return 'priority-low';
        },
        getPriorityText(priority) {
            const priorityLevel = parseInt(priority);
            if (priorityLevel >= 8) return 'High';
            if (priorityLevel >= 5) return 'Medium';
            return 'Low';
        },
        getAccountStatusClass(status) {
            return `account-status-${status}`;
        },
        getProfilePicture(filename) {
            // Adjust this path based on your backend configuration
            return `/uploads/profiles/${filename}`;
        },
        getInitials(firstName, lastName) {
            const first = firstName ? firstName.charAt(0).toUpperCase() : '';
            const last = lastName ? lastName.charAt(0).toUpperCase() : '';
            return first + last;
        },
    },
};
</script>

<style lang="scss" scoped>
.lead-preview-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;

    .preview-header {
        padding: 20px;
        border-bottom: 1px solid #eee;
        background: #f8f9fa;

        .header-content {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;

            .header-icon {
                margin-right: 15px;

                img {
                    width: 32px;
                    height: 32px;
                }
            }

            .header-info {
                flex: 1;

                .lead-title {
                    margin: 0 0 5px 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                }

                .lead-meta {
                    margin: 0 0 3px 0;
                    color: #666;
                }
            }

            .priority-badge {
                .priority-high {
                    background: #ffebee;
                    color: #d32f2f;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .priority-medium {
                    background: #fff3e0;
                    color: #f57c00;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .priority-low {
                    background: #e8f5e8;
                    color: #2e7d32;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                    text-transform: uppercase;
                }
            }
        }
    }

    .preview-body {
        flex: 1;
        padding: 20px;
        overflow-y: auto;

        .lead-details {
            .detail-section {
                margin-bottom: 25px;

                .section-title {
                    font-size: 13px;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 15px 0;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #e3f2fd;
                }

                .detail-row {
                    display: flex;
                    align-items: flex-start;
                    margin-bottom: 12px;
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;

                    label {
                        font-weight: 600;
                        color: #555;
                        min-width: 120px;
                        margin-right: 10px;
                    }

                    span,
                    .description-content {
                        color: #333;

                        &.lead-value {
                            background: #e8f5e8;
                            color: #2e7d32;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 600;
                        }

                        &.date-range {
                            background: #fff3e0;
                            color: #f57c00;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.attachment {
                            background: #e3f2fd;
                            color: #1976d2;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.percentage-badge {
                            background: #f3e5f5;
                            color: #7b1fa2;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                        }

                        &.account-name {
                            font-weight: 600;
                            color: #1976d2;
                        }

                        &.email-link {
                            color: #1976d2;
                            text-decoration: underline;
                        }

                        &.website-link {
                            color: #1976d2;
                            text-decoration: underline;
                        }

                        &.category-badge {
                            background: #fce4ec;
                            color: #c2185b;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                            text-transform: capitalize;
                        }

                        &.worktype-badge {
                            background: #e0f2f1;
                            color: #00695c;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-weight: 500;
                            text-transform: capitalize;
                        }

                        &.account-status-active {
                            background: #e8f5e8;
                            color: #2e7d32;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }

                        &.account-status-passive {
                            background: #fff3e0;
                            color: #f57c00;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }

                        &.account-status-inactive {
                            background: #ffebee;
                            color: #d32f2f;
                            padding: 3px 8px;
                            border-radius: 12px;
                            font-weight: 600;
                            text-transform: capitalize;
                        }
                    }

                    .description-content {
                        flex: 1;
                        line-height: 1.5;
                        white-space: pre-wrap;
                    }
                }
            }

            .tags-list {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;

                .tag-item {
                    background: #e1f5fe;
                    color: #0277bd;
                    padding: 6px 12px;
                    border-radius: 16px;
                    font-weight: 500;
                    border: 1px solid #b3e5fc;
                }
            }

            .assignees-list {
                .assignee-item {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin-bottom: 8px;

                    .assignee-avatar {
                        margin-right: 12px;

                        img {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            object-fit: cover;
                        }

                        .avatar-placeholder {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            background: #1976d2;
                            color: white;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 12px;
                            font-weight: 600;
                        }
                    }

                    .assignee-info {
                        .assignee-name {
                            font-weight: 600;
                            color: #333;
                        }
                    }
                }
            }

            .contacts-list {
                .contact-item {
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin-bottom: 8px;

                    .contact-info {
                        display: flex;
                        flex-direction: column;
                        gap: 3px;

                        .contact-name {
                            font-weight: 600;
                            color: #333;
                        }

                        .contact-email {
                            color: #1976d2;
                        }

                        .contact-title {
                            color: #666;
                            font-style: italic;
                        }

                        .contact-details {
                            display: flex;
                            gap: 10px;
                            margin-top: 3px;

                            .contact-phone {
                                background: #e3f2fd;
                                color: #1976d2;
                                padding: 2px 8px;
                                border-radius: 12px;
                                font-weight: 500;
                            }

                            .contact-worktype {
                                background: #e0f2f1;
                                color: #00695c;
                                padding: 2px 8px;
                                border-radius: 12px;
                                font-weight: 500;
                                text-transform: capitalize;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>